import {
  type ReframeAsset,
  validateReframeAssets as validateReframeAssetsBase,
  type ValidationResult,
} from '@frameio/graphql-client/graphql/features/assets/queries/reframeValidation';
import { datadogLogger } from '@frameio/telemetry/datadog/logger';

/** Reframe validation that also logs unsupported codecs to Datadog. */
export function validateReframeAssets(
  assets: ReframeAsset[]
): ValidationResult {
  return validateReframeAssetsBase(assets, {
    onUnsupportedCodec: (codec) =>
      datadogLogger.warn('firefly-reframe-codec-not-supported', { codec }),
  });
}

// The backend accepts Unicode-aware alphanumeric keywords: letters and numbers
// of any script (e.g. "café", CJK) plus spaces, hyphens, and underscores, with
// commas separating keywords. Punctuation, symbols, and emoji are rejected.
const INVALID_FOCUS_SUBJECT_RE = /[^\p{L}\p{N} _,-]/u;

// The focus subject text is split on commas into keywords on the backend
export const MAX_FOCUS_SUBJECT_KEYWORDS = 10;
export const MAX_FOCUS_SUBJECT_KEYWORD_LENGTH = 1000;

export type FocusSubjectErrorKind =
  | 'invalid_characters'
  | 'too_many_keywords'
  | 'keyword_too_long';

/**
 * Validates the focus-subject text against the reframe backend's rules.
 * @param value - The focus-subject text entered by the user.
 * @returns The first failing rule, or null when the text is valid.
 */
export function validateFocusSubject(
  value: string
): FocusSubjectErrorKind | null {
  if (INVALID_FOCUS_SUBJECT_RE.test(value)) {
    return 'invalid_characters';
  }
  const keywords = value
    .split(',')
    .map((keyword) => keyword.trim())
    .filter((keyword) => keyword !== '');
  if (keywords.length > MAX_FOCUS_SUBJECT_KEYWORDS) {
    return 'too_many_keywords';
  }
  if (
    keywords.some(
      (keyword) => keyword.length > MAX_FOCUS_SUBJECT_KEYWORD_LENGTH
    )
  ) {
    return 'keyword_too_long';
  }
  return null;
}
